import React, { useState } from 'react';
import { ReactComponent as MapIcon } from '@zonofi/svg-icons/map-icon.svg';
import { ReactComponent as SegmentIcon } from '@zonofi/svg-icons/segment-icon.svg';
import { TouchableOpacity } from '@zonofi/seller-hub/common';

interface CustomerTagViewProps {
  customer?: {
    customerLifeCycleGroups?: any[];
    customerCfas?: any[];
    routes?: string[];
    segments?: string[];
  };
}

const LIFECYCLE_COLOR_PALETTE = [
  '#0067ff',
  '#8c3cff',
  '#2abb7f',
  '#916100',
  '#dd360c',
  '#127954',
];

const getLifecycleTagColor = (group: any): string => {
  const name = String(group?.name ?? '');
  const hash = name
    .toLowerCase()
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return LIFECYCLE_COLOR_PALETTE[hash % LIFECYCLE_COLOR_PALETTE.length];
};

const useShowMore = <T,>(items: T[], initialCount = 1) => {
  const [showAll, setShowAll] = useState(false);

  const visibleItems = showAll ? items : items.slice(0, initialCount);
  const remainingCount = items.length - initialCount;

  return {
    showAll,
    setShowAll,
    visibleItems,
    remainingCount,
    hasMore: items.length > initialCount,
  };
};

export const CustomerTagView: React.FC<CustomerTagViewProps> = ({
  customer,
}) => {
  const lifecycleGroups = customer?.customerLifeCycleGroups || [];
  const routes = customer?.routes || [];
  const segments = customer?.segments || [];

  const routeSegmentList = [
    ...routes.map((r) => ({ type: 'route', label: r })),
    ...segments.map((s) => ({ type: 'segment', label: s })),
  ];

  const lifecycle = useShowMore(lifecycleGroups, 2);
  const routeSegment = useShowMore(routeSegmentList, 2);

  return (
    <div className="customer-details-tag-view">
      {/* Lifecycle Groups */}
      {lifecycle.visibleItems.map((group, index) => (
        <div
          key={`${group?.name}-${index}`}
          className="customer-details-tag-view-group font-semibold"
          style={{ backgroundColor: getLifecycleTagColor(group) }}
        >
          {group?.emojiId && <span>{group?.emojiId}</span>} {group?.name}
        </div>
      ))}

      {!lifecycle.showAll && lifecycle.hasMore && (
        <TouchableOpacity
          className="customer-details-tag-location"
          onPress={() => lifecycle.setShowAll(true)}
        >
          <div className="customer-details-tag-location-text font-semibold">
            +{lifecycle.remainingCount}
          </div>
        </TouchableOpacity>
      )}

      {/* Divider */}
      {lifecycleGroups.length > 0 && routeSegmentList.length > 0 && (
        <div className="customer-details-tag-view-divider" />
      )}

      {/* Routes + Segments */}
      {routeSegment.visibleItems.map((item, index) => (
        <div
          key={`${item.type}-${item.label}-${index}`}
          className="customer-details-tag-location"
        >
          {item.type === 'route' ? (
            <MapIcon color="#262626" />
          ) : (
            <SegmentIcon width={12} height={12} color="#262626" />
          )}

          <div className="customer-details-tag-location-text font-semibold">
            {item.label}
          </div>
        </div>
      ))}

      {!routeSegment.showAll && routeSegment.hasMore && (
        <TouchableOpacity
          className="customer-details-tag-location"
          onPress={() => routeSegment.setShowAll(true)}
        >
          <div className="customer-details-tag-location-text font-semibold">
            +{routeSegment.remainingCount}
          </div>
        </TouchableOpacity>
      )}
    </div>
  );
};
